import React, { useEffect, useRef, useState } from 'react';
import ModernCard from '../components/ModernCard';
import ModernButton from '../components/ModernButton';
import PageWrapper from '../components/PageWrapper';
import PageLayout from '../components/PageLayout';
import { useDashboardStats } from '../hooks/useDashboardStats';
import { useCurrency } from '../contexts/CurrencyContext';
import ClientForm from '../components/ClientForm';
import OrderForm from '../components/OrderForm';
import { 
  Users, 
  Package, 
  DollarSign, 
  Calendar,
  TrendingUp,
  Activity,
  BarChart3,
  PieChart,
  RefreshCw,
  MoreHorizontal
} from 'lucide-react';

const DashboardExample: React.FC = () => {
  const { stats, loading, error, refetch } = useDashboardStats();
  const { currency } = useCurrency();
  
  // États pour les modales
  const [isClientFormOpen, setIsClientFormOpen] = useState(false);
  const [isOrderFormOpen, setIsOrderFormOpen] = useState(false);
  const [isQuickActionsOpen, setIsQuickActionsOpen] = useState(false);
  const quickActionsRef = useRef<HTMLDivElement | null>(null);

  // Fonctions de gestion des actions
  const handleAddClient = () => {
    setIsQuickActionsOpen(false);
    setIsClientFormOpen(true);
  };

  const handleCreateOrder = () => {
    setIsQuickActionsOpen(false);
    setIsOrderFormOpen(true);
  };

  const handleCreateTask = () => {
    setIsQuickActionsOpen(false);
    // Rediriger vers la page des tâches avec action=create pour ouvrir automatiquement le modal
    window.location.href = '/dashboard/tasks?action=create';
  };

  const handleCreateInvoice = () => {
    setIsQuickActionsOpen(false);
    // Rediriger vers la page de facturation
    window.location.href = '/invoices';
  };

  const handleFormSuccess = () => {
    setIsClientFormOpen(false);
    setIsOrderFormOpen(false);
    refetch(); // Rafraîchir les statistiques
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (quickActionsRef.current && !quickActionsRef.current.contains(event.target as Node)) {
        setIsQuickActionsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsQuickActionsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <p className="ml-3 text-gray-600 dark:text-slate-400">Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-400 font-semibold">Error loading dashboard</p>
        <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">{error}</p>
        <ModernButton onClick={refetch} className="mt-4">
          <RefreshCw className="mr-2" size={16} />
          Retry
        </ModernButton>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-600 dark:text-slate-400">No data available</p>
      </div>
    );
  }

  return (
    <PageWrapper>
      <PageLayout
        title="Dashboard"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ModernButton
              onClick={refetch}
              size="sm"
              className="px-2 sm:px-3"
            >
              <RefreshCw className="sm:mr-2" size={16} />
              <span className="hidden sm:inline">Refresh</span>
            </ModernButton>
            <div className="relative" ref={quickActionsRef}>
              <ModernButton
                onClick={() => setIsQuickActionsOpen((prev) => !prev)}
                size="sm"
                className="px-2 sm:px-3"
              >
                <MoreHorizontal size={16} className="sm:hidden" />
                <span className="hidden sm:inline">Quick Actions</span>
              </ModernButton>
              {isQuickActionsOpen && (
                <div className="absolute top-full right-0 mt-2 w-48 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg z-50">
                  <div className="py-2">
                    <button 
                      onClick={handleAddClient}
                      className="w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-slate-100 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-2"
                    >
                      <Users size={16} />
                      Add Client
                    </button>
                    <button 
                      onClick={handleCreateOrder}
                      className="w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-slate-100 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-2"
                    >
                      <Package size={16} />
                      Create Order
                    </button>
                    <button 
                      onClick={handleCreateTask}
                      className="w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-slate-100 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-2"
                    >
                      <Calendar size={16} />
                      Create Task
                    </button>
                    <button 
                      onClick={handleCreateInvoice}
                      className="w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-slate-100 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-2"
                    >
                      <DollarSign size={16} />
                      Create Invoice
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        }
      >
        {/* Stats Cards - Style Shopify */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 flex-shrink-0">
        <ModernCard 
          title="Total Clients"
          className="h-full"
        >
          <div className="mt-4">
            <div className="text-2xl font-semibold theme-text-primary mb-1">{stats.totalClients.toLocaleString()}</div>
            <div className={`flex items-center text-xs theme-text-secondary ${
              stats.clientsGrowth >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              <TrendingUp size={12} className="mr-1" />
              {stats.clientsGrowth >= 0 ? '+' : ''}{stats.clientsGrowth}% this month
            </div>
          </div>
        </ModernCard>

        <ModernCard 
          title="Orders"
          className="h-full"
        >
          <div className="mt-4">
            <div className="text-2xl font-semibold theme-text-primary mb-1">{stats.totalOrders.toLocaleString()}</div>
            <div className={`flex items-center text-xs theme-text-secondary ${
              stats.ordersGrowth >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              <Activity size={12} className="mr-1" />
              {stats.ordersGrowth >= 0 ? '+' : ''}{stats.ordersGrowth}% this month
            </div>
          </div>
        </ModernCard>

        <ModernCard 
          title="Revenue"
          className="h-full"
        >
          <div className="mt-4">
            <div className="text-2xl font-semibold theme-text-primary mb-1">
              {new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(stats.totalRevenue)}
            </div>
            <div className={`flex items-center text-xs theme-text-secondary ${
              stats.revenueGrowth >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              <TrendingUp size={12} className="mr-1" />
              {stats.revenueGrowth >= 0 ? '+' : ''}{stats.revenueGrowth}% this month
            </div>
          </div>
        </ModernCard>

        <ModernCard 
          title="Completion Rate"
          className="h-full"
        >
          <div className="mt-4">
            <div className="text-2xl font-semibold theme-text-primary mb-1">{stats.completionRate}%</div>
            <div className="flex items-center text-xs theme-text-secondary">
              <Calendar size={12} className="mr-1" />
              Avg. {stats.averageDeliveryTime.toFixed(1)} days
            </div>
          </div>
        </ModernCard>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:flex-1">
        <ModernCard 
          title="Monthly Revenue" 
          icon={<BarChart3 size={20} className="text-gray-900 dark:text-slate-100" />}
          className="h-full min-h-[320px]"
        >
          <div className="h-64 flex items-center justify-center">
            <div className="w-full">
              <div className="grid grid-cols-6 gap-3 h-full">
                {stats.monthlyRevenue.map((month, index) => {
                  const maxRevenue = Math.max(...stats.monthlyRevenue.map(m => m.revenue));
                  const height = maxRevenue > 0 ? (month.revenue / maxRevenue) * 100 : 0;
                  const isHighest = month.revenue === maxRevenue;
                  return (
                    <div key={index} className="flex flex-col justify-end group">
                      <div className="relative">
                        <div 
                          className={`rounded-t-lg transition-all duration-300 group-hover:scale-105 ${
                            isHighest 
                              ? 'bg-gradient-to-t from-emerald-500 via-emerald-400 to-emerald-300 shadow-lg shadow-emerald-500/25' 
                              : 'bg-gradient-to-t from-blue-500 via-blue-400 to-blue-300'
                          }`}
                          style={{ height: `${Math.max(height, 8)}%` }}
                        />
                        {isHighest && (
                          <div className="absolute -top-1 left-1/2 transform -translate-x-1/2">
                            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-slate-400 mt-2 text-center font-medium">{month.month}</div>
                      <div className={`text-xs text-center font-semibold transition-colors ${
                        isHighest ? 'text-emerald-400' : 'text-gray-500'
                      }`}>
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0 }).format(month.revenue)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </ModernCard>

        <ModernCard 
          title="Orders by Status" 
          icon={<PieChart size={20} className="text-gray-900 dark:text-slate-100" />}
          className="h-full"
        >
          <div className="h-64 flex items-center justify-center">
            <div className="w-full">
              <div className="space-y-4">
                {stats.ordersByStatus.map((status, index) => {
                  // Déterminer la couleur selon le statut
                  let colorSet;
                  if (status.status === 'in_progress') {
                    colorSet = { bg: 'bg-gray-500', ring: 'ring-gray-500/20', text: 'text-gray-900 dark:text-slate-100 dark:text-slate-400' };
                  } else if (status.status === 'completed') {
                    colorSet = { bg: 'bg-green-500', ring: 'ring-green-500/20', text: 'text-green-600 dark:text-green-400' };
                  } else if (status.status === 'pending') {
                    colorSet = { bg: 'bg-yellow-500', ring: 'ring-yellow-500/20', text: 'text-yellow-600 dark:text-yellow-400' };
                  } else {
                    const colors = [
                      { bg: 'bg-blue-500', ring: 'ring-blue-500/20', text: 'text-[rgb(var(--brand))]' },
                      { bg: 'bg-amber-500', ring: 'ring-amber-500/20', text: 'text-amber-400' },
                      { bg: 'bg-rose-500', ring: 'ring-rose-500/20', text: 'text-rose-400' },
                      { bg: 'bg-purple-500', ring: 'ring-purple-500/20', text: 'text-purple-400' }
                    ];
                    colorSet = colors[index % colors.length];
                  }
                  const total = stats.ordersByStatus.reduce((sum, s) => sum + s.count, 0);
                  const percentage = total > 0 ? Math.round((status.count / total) * 100) : 0;
                  const isHighest = status.count === Math.max(...stats.ordersByStatus.map(s => s.count));
                  
                  // Formater le statut pour l'affichage
                  const formatStatus = (statusValue: string) => {
                    if (statusValue === 'in_progress') return 'In Progress';
                    return statusValue.split('_').map(word => 
                      word.charAt(0).toUpperCase() + word.slice(1)
                    ).join(' ');
                  };
                  
                  return (
                    <div key={index} className={`group relative p-3 rounded-lg border transition-all duration-300 hover:scale-105 ${
                      isHighest 
                        ? `${colorSet.ring} bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-600` 
                        : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-500'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-4 h-4 rounded-full ${colorSet.bg} shadow-lg ${
                            isHighest ? 'animate-pulse' : ''
                          }`} />
                          <span className={`text-sm font-medium ${
                            isHighest ? colorSet.text : 'theme-text-primary'
                          }`}>
                            {formatStatus(status.status)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-bold theme-text-primary">
                            {status.count}
                          </div>
                          <div className={`text-xs px-2 py-1 rounded-full font-semibold ${
                            isHighest 
                              ? `${colorSet.bg} text-gray-900 dark:text-slate-100` 
                              : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300'
                          }`}>
                            {percentage}%
                          </div>
                        </div>
                      </div>
                      {isHighest && (
                        <div className="absolute -top-1 -right-1">
                          <div className="w-3 h-3 bg-emerald-400 rounded-full animate-ping"></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </ModernCard>
      </div>

      {/* Recent Activity & Top Clients */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 flex-shrink-0">
        <ModernCard 
          title="Recent Orders" 
          icon={<Activity size={20} className="text-gray-900 dark:text-slate-100" />}
          className="h-full"
        >
          <div className="space-y-4">
            {stats.recentOrders.length === 0 ? (
              <p className="text-gray-600 dark:text-slate-400 text-center py-4">No recent orders</p>
            ) : (
              stats.recentOrders.map((order, index) => (
                <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 border border-gray-200 dark:border-slate-700 transition-colors">
                  <div className={`w-2 h-2 rounded-full ${
                    order.status === 'completed' ? 'bg-green-500' :
                    order.status === 'in_progress' ? 'bg-gray-500' :
                    order.status === 'pending' ? 'bg-amber-500' :
                    'bg-gray-400'
                  }`} />
                  <div className="flex-1">
                    <p className="theme-text-primary text-sm font-medium">{order.title}</p>
                    <p className="theme-text-secondary text-xs">
                      {order.client_name} • {new Date(order.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    order.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                    order.status === 'in_progress' ? 'bg-gray-100 dark:bg-gray-700/50 text-gray-800 dark:text-slate-400' :
                    order.status === 'pending' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300' :
                    'bg-gray-100 dark:bg-gray-700/50 text-gray-800 dark:text-slate-400'
                  }`}>
                    {order.status === 'in_progress' ? 'In Progress' : 
                     order.status === 'completed' ? 'Completed' :
                     order.status === 'pending' ? 'Pending' : order.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </ModernCard>

        <ModernCard 
          title="Top Clients" 
          icon={<Users size={20} className="text-gray-900 dark:text-slate-100" />}
          className="h-full"
        >
          <div className="space-y-4">
            {stats.topClients.length === 0 ? (
              <p className="text-gray-600 dark:text-slate-400 text-center py-4">No client data</p>
            ) : (
              stats.topClients.map((client, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 border border-gray-200 dark:border-slate-700 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white text-sm font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <p className="theme-text-primary text-sm font-medium">{client.name}</p>
                      <p className="theme-text-secondary text-xs">{client.orders} orders</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="theme-text-primary text-sm font-medium">
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(client.revenue)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </ModernCard>
      </div>

      {/* Modales */}
      <ClientForm
        isOpen={isClientFormOpen}
        onClose={() => setIsClientFormOpen(false)}
        onSuccess={handleFormSuccess}
      />

      <OrderForm
        isOpen={isOrderFormOpen}
        onClose={() => setIsOrderFormOpen(false)}
        onSuccess={handleFormSuccess}
      />
      </PageLayout>
    </PageWrapper>
  );
};

export default DashboardExample;